/**
 * DRBFM History Page
 *
 * Displays a list of past DRBFM analysis sessions with ability to:
 * - View session details and results
 * - Export results
 * - Delete sessions
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useDRBFMSessions } from '@hooks/useDRBFM';
import { useTenantContext } from '@hooks/useTenantContext';
import { useMainlineStatus } from '@components/drbfm/workflow/mainline/useMainlineStatus';
import DeleteSessionDialog from '@components/drbfm/history/DeleteSessionDialog';
import DismissibleErrorAlert from '@components/drbfm/history/DismissibleErrorAlert';
import HistoryPageHeader from '@components/drbfm/history/HistoryPageHeader';
import { sessionOpenPath, sessionViewResultsPath } from '@components/drbfm/history/sessionNavigation';
import SessionsTable from '@components/drbfm/history/SessionsTable';
import {
  useConvertToVerification,
  useSessionDeletion,
  useSessionExport,
  useView3DNavigation,
} from '@components/drbfm/history/useDrbfmSessionActions';
import { DRBFMSession } from '../types';
import { ConvertToVerificationDialog } from '@components/drbfm';

const DRBFMHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sessions, isLoading, error, fetchSessions, deleteSession, clearError } =
    useDRBFMSessions();

  const view3D = useView3DNavigation();
  const exporting = useSessionExport();
  const convert = useConvertToVerification();
  const deletion = useSessionDeletion(deleteSession);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const { tenantId } = useTenantContext();
  // Only a confirmed flag opens the workspace; checking or unavailable keeps the destination main has.
  const mainlineEnabled = useMainlineStatus(tenantId) === 'enabled';
  const handleViewSession = (session: DRBFMSession) => navigate(
    session.status === 'completed'
      ? sessionViewResultsPath(session, mainlineEnabled)
      : sessionOpenPath(session),
  );

  const startNewAnalysis = () => navigate('/drbfm');

  return (
    <div className="mx-auto max-w-[1200px] p-6">
      <HistoryPageHeader
        isLoading={isLoading}
        onBack={() => navigate('/drbfm')}
        onRefresh={() => fetchSessions()}
        onNewAnalysis={startNewAnalysis}
      />

      {error && <DismissibleErrorAlert message={error} onDismiss={clearError} />}
      {exporting.error && (
        <DismissibleErrorAlert message={exporting.error} onDismiss={exporting.clearError} />
      )}
      {convert.error && (
        <DismissibleErrorAlert message={convert.error} onDismiss={convert.clearError} />
      )}

      {isLoading && sessions.length === 0 ? (
        <Card className="flex justify-center p-4 py-8">
          <Spinner />
        </Card>
      ) : sessions.length === 0 ? (
        <Card className="p-4">
          <EmptyState
            variant="bare"
            icon={History}
            title={t('drbfm.history.noSessions', '分析履歴がありません')}
            action={
              <Button onClick={startNewAnalysis}>
                <Plus strokeWidth={1.9} />
                <span className="revy-cjk">
                  {t('drbfm.history.startFirst', '最初の分析を始める')}
                </span>
              </Button>
            }
          />
        </Card>
      ) : (
        <SessionsTable
          sessions={sessions}
          view3DSessionId={view3D.loadingSessionId}
          exportingSessionId={exporting.exportingSessionId}
          isConvertBusy={convert.isLoadingResults}
          onView={handleViewSession}
          onView3D={view3D.view3D}
          onExport={exporting.exportSession}
          onConvert={convert.start}
          onDelete={deletion.request}
        />
      )}

      <DeleteSessionDialog
        open={deletion.isOpen}
        itemName={deletion.target?.itemName || ''}
        isDeleting={deletion.isDeleting}
        onCancel={deletion.cancel}
        onConfirm={deletion.confirm}
      />

      {convert.session && convert.results && (
        <ConvertToVerificationDialog
          open={convert.isOpen}
          onClose={convert.close}
          drbfmSessionId={convert.session.id}
          drbfmItemName={convert.session.itemName}
          failureModes={convert.results.failureModes || []}
          designChanges={convert.results.designChanges || []}
          countermeasures={convert.results.countermeasures || []}
        />
      )}
    </div>
  );
};

export default DRBFMHistoryPage;
